import { el, clear } from '../../core/dom.js';
import { navigate } from '../../core/router.js';
import { withProduct } from '../../core/route-map.js';
import { empty } from '../../components/empty.js';
import { listProducts } from './actions.js';
import { isAdmin, normalizeSection, sectionDenied } from './util.js';
import { renderHeader, renderSubnav } from './nav.js';
import { mountTeam } from './team.js';
import { mountProducts } from './products.js';
import { mountModels } from './models.js';
import { mountBillingSupport } from './billing.js';
import { mountOrganizations } from './organizations.js';
import { mountWorkspace } from './workspace.js';
import { mountNotifications } from './notifications.js';
import { mountAudit } from './audit.js';
import { mountActivity } from './activity.js';

/**
 * Settings screen (plan 4 R8). Sections: team, products, models, organizations
 * (super-admin), billing, support. Connectors / knowledge deep-links redirect out.
 * @param {{
 *   section?: string,
 *   productId: string,
 *   productName?: string,
 *   me: object,
 * }} opts
 */
export function renderSettings(opts) {
  const resolved = normalizeSection(opts.section);
  if (resolved.kind === 'external') {
    navigate(withProduct(resolved.path, opts.productId));
    return el('div', { class: 'set' }, el('p', { class: 'set-hint' }, 'Opening…'));
  }

  const section = resolved.section;
  const admin = isAdmin(opts.me);
  const denied = sectionDenied(section, opts.me);
  const root = el('div', { class: 'set' });
  const head = el('div');
  const layout = el('div', { class: 'set-layout' });
  const navHost = el('div');
  const main = el('div', { class: 'set-main' });
  const live = el('p', { class: 'set-sr', 'aria-live': 'polite' });
  layout.append(navHost, main);
  root.append(head, layout, live);

  const ui = {
    dead: false,
    section,
    products: [],
    sectionCtl: null,
  };

  root.addEventListener('bonnie:unmount', () => {
    ui.dead = true;
    if (ui.sectionCtl && ui.sectionCtl.dispose) ui.sectionCtl.dispose();
  });

  function dead() {
    return ui.dead;
  }

  function paintChrome() {
    clear(head);
    clear(navHost);
    // Organizations brings its own page title; Settings subnav stays.
    if (section !== 'organizations' || denied) {
      head.append(renderHeader(denied && section === 'organizations' ? 'organizations' : section, {
        productId: opts.productId,
        me: opts.me,
        isAdmin: admin,
        onInvite: section === 'team' && ui.sectionCtl && ui.sectionCtl.openInvite
          ? () => ui.sectionCtl.openInvite()
          : undefined,
        onAddProduct: section === 'products' ? true : undefined,
      }));
    }
    navHost.append(renderSubnav(section, { productId: opts.productId, me: opts.me }));
  }

  function mountSection() {
    if (ui.sectionCtl && ui.sectionCtl.dispose) ui.sectionCtl.dispose();
    clear(main);
    const host = el('div', { class: 'set-section' });
    main.append(host);
    const shared = {
      host,
      me: opts.me,
      isAdmin: admin,
      productId: opts.productId,
      products: ui.products,
      dead,
    };
    if (denied) {
      ui.sectionCtl = { dispose() {} };
      host.append(empty({ title: 'No access', body: denied }));
    } else if (section === 'workspace') ui.sectionCtl = mountWorkspace(shared);
    else if (section === 'notifications') ui.sectionCtl = mountNotifications(shared);
    else if (section === 'audit') ui.sectionCtl = mountAudit(shared);
    else if (section === 'activity') ui.sectionCtl = mountActivity(shared);
    else if (section === 'products') ui.sectionCtl = mountProducts(shared);
    else if (section === 'models') ui.sectionCtl = mountModels(shared);
    else if (section === 'organizations') ui.sectionCtl = mountOrganizations(shared);
    else if (section === 'billing' || section === 'support') {
      ui.sectionCtl = mountBillingSupport({ ...shared, section });
    } else {
      ui.sectionCtl = mountTeam(shared);
    }
    paintChrome();
  }

  async function loadProducts() {
    const data = await listProducts('active');
    if (dead()) return;
    ui.products = (data && !data.error && data.products) || [];
    if (ui.sectionCtl && ui.sectionCtl.setProducts) ui.sectionCtl.setProducts(ui.products);
  }

  mountSection();
  loadProducts();
  live.textContent = `Settings · ${section}`;
  return root;
}
